import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, ChevronRight, ChevronDown, Loader2, Layers,
  ExternalLink, Ruler, FlaskConical, Scale, X, ArrowRightLeft,
  Heart, ShoppingBag, Share2, Trash2, ZoomIn, FileText
} from 'lucide-react';
import { Button } from '../ui/button';
import { ShortlistQuoteDialog } from '../configurator/fabric-gallery/ShortlistQuoteDialog';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { resolveImageUrl } from '../../lib/imageCache';
import { SwatchZoomModal } from './SwatchZoomModal';
import { RecentlyViewedStrip } from './RecentlyViewedStrip';
import { addRecentlyViewed } from '../../lib/recentlyViewed';
import { tierLabels } from '../../lib/fabricLabels';
import { StockDot } from './StockDot';
import { brandLabel, brandSearchTerm } from '../../lib/brandNames';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Favorites helper (localStorage + optional server sync) ── */
function getFavSessionId() {
  let sid = localStorage.getItem('fabric_fav_session');
  if (!sid) {
    sid = 'fav_' + Math.random().toString(36).slice(2, 12);
    localStorage.setItem('fabric_fav_session', sid);
  }
  return sid;
}

function getLocalFavs() {
  try { return JSON.parse(localStorage.getItem('fabric_favorites') || '[]'); }
  catch { return []; }
}

function setLocalFavs(ids) {
  localStorage.setItem('fabric_favorites', JSON.stringify(ids));
}

/* ── Main Public Fabric Browser ── */
export const PublicFabricBrowser = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedColl, setExpandedColl] = useState({});
  const [expandedMat, setExpandedMat] = useState({});
  const [compareList, setCompareList] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [favIds, setFavIds] = useState(getLocalFavs);
  const [showFavs, setShowFavs] = useState(false);
  const [favColors, setFavColors] = useState([]);
  const [favMissing, setFavMissing] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [loadingFavs, setLoadingFavs] = useState(false);
  const [zoomColor, setZoomColor] = useState(null);
  const openZoom = (color) => { addRecentlyViewed(color); setZoomColor(color); };

  const fetchData = async (q) => {
    setLoading(true);
    try {
      const params = q ? { search: brandSearchTerm(q) } : {};
      const { data: resp } = await axios.get(`${API}/api/public/fabric-browser`, { params });
      setData(resp);
      if (q && resp.collections?.length) {
        const init = {};
        resp.collections.forEach(c => { init[c.collection_id] = true; });
        setExpandedColl(init);
      }
    } catch (e) {
      console.error('Fabric browser error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(''); }, []);

  // Sync favorites to server periodically
  const syncFavsToServer = useCallback(async (ids) => {
    try {
      const sid = getFavSessionId();
      await axios.post(`${API}/api/fabric-collections/favorites`, {
        session_id: sid,
        color_ids: ids
      });
    } catch (e) { /* silent */ }
  }, []);

  const toggleFav = useCallback((colorCode) => {
    setFavIds(prev => {
      const next = prev.includes(colorCode)
        ? prev.filter(c => c !== colorCode)
        : [...prev, colorCode];
      setLocalFavs(next);
      syncFavsToServer(next);
      return next;
    });
  }, [syncFavsToServer]);

  const isFav = (code) => favIds.includes(code);

  // Fetch enriched fav data for drawer
  const openFavsDrawer = async () => {
    setShowFavs(true);
    if (favIds.length === 0) return;
    setLoadingFavs(true);
    try {
      const sid = getFavSessionId();
      await syncFavsToServer(favIds);
      const { data: resp } = await axios.get(`${API}/api/fabric-collections/favorites/${sid}`);
      setFavColors(resp.colors || []);
      setFavMissing(resp.missing || []);
    } catch {
      // Fallback: resolve against the already-loaded browser data
      const local = [];
      (data?.collections || []).forEach(coll => (coll.materials || []).forEach(m => (m.colors || []).forEach(c => {
        const key = favIds.find(f => f.toLowerCase() === (c.code || '').toLowerCase() || f === c.id);
        if (key) local.push({ ...c, source_color_id: key, collection_name: coll.collection_name || coll.name });
      })));
      setFavColors(local);
      setFavMissing(favIds.filter(f => !local.some(c => c.source_color_id === f)));
    } finally { setLoadingFavs(false); }
  };

  const removeFav = (code) => {
    toggleFav(code);
    setFavColors(prev => prev.filter(c => (c.source_color_id || '').toLowerCase() !== (code || '').toLowerCase()));
    setFavMissing(prev => prev.filter(c => c.toLowerCase() !== (code || '').toLowerCase()));
  };

  const clearFavs = () => {
    if (!window.confirm('Remove all favorites?')) return;
    setFavIds([]); setLocalFavs([]); syncFavsToServer([]);
    setFavColors([]); setFavMissing([]);
  };

  const shareFavorites = async () => {
    const sid = getFavSessionId();
    const url = `${window.location.origin}/fabrics?favorites=${sid}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Favorites link copied to clipboard!');
    } catch {
      toast.info(`Share link: ${url}`);
    }
  };

  const debounceRef = React.useRef(null);
  const handleSearch = (e) => {
    const v = e.target.value;
    setSearch(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchData(v), 400);
  };

  const toggleColl = (id) => setExpandedColl(p => ({ ...p, [id]: !p[id] }));
  const toggleMat = (id) => setExpandedMat(p => ({ ...p, [id]: !p[id] }));

  const toggleCompare = (color) => {
    const key = color.code || color.id;
    setCompareList(prev => {
      const exists = prev.find(c => (c.code || c.id) === key);
      if (exists) return prev.filter(c => (c.code || c.id) !== key);
      if (prev.length >= 4) return prev;
      return [...prev, color];
    });
  };

  const isInCompare = (key) => compareList.some(c => (c.code || c.id) === key);

  return (
    <div data-testid="public-fabric-browser">
      <RecentlyViewedStrip onSelect={openZoom} className="mb-6" />
      {/* Search + Favorites button */}
      <div className="flex items-center gap-3 mb-8">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <Input
            value={search}
            onChange={handleSearch}
            placeholder="Search fabrics by name, code, or collection..."
            className="pl-12 h-12 text-base rounded-full border-neutral-300 focus:border-amber-500"
            data-testid="fabric-browser-search"
          />
          {loading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-amber-500" />}
        </div>
        <Button
          variant="outline"
          onClick={openFavsDrawer}
          className="relative h-12 px-5 rounded-full border-neutral-300 hover:border-rose-400 hover:bg-rose-50 transition-colors gap-2"
          data-testid="favorites-btn"
        >
          <Heart className={`w-5 h-5 ${favIds.length > 0 ? 'fill-rose-500 text-rose-500' : 'text-neutral-400'}`} />
          <span className="text-sm font-medium">Favorites</span>
          {favIds.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {favIds.length}
            </span>
          )}
        </Button>
      </div>

      {/* Compare floating bar */}
      <AnimatePresence>
        {compareList.length >= 2 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-neutral-900 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-4"
          >
            <ArrowRightLeft className="w-5 h-5 text-amber-400" />
            <span className="text-sm font-medium">{compareList.length} fabrics selected</span>
            <div className="flex gap-1">
              {compareList.map(c => (
                <div key={c.code || c.id} className="w-8 h-8 rounded-full border-2 border-white/30 overflow-hidden">
                  <img src={resolveImageUrl(c.image_url, 128)} alt={c.name ? `${c.name} fabric swatch` : ''} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <Button size="sm" onClick={() => setShowCompare(true)} className="bg-amber-600 hover:bg-amber-700 rounded-full text-xs">
              Compare
            </Button>
            <button onClick={() => setCompareList([])} className="text-white/60 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      <ShortlistQuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} fabrics={favColors.map(c => ({ ...c, id: c.id || c.source_color_id }))} />

      {/* Compare Modal */}
      {showCompare && (
        <TextureCompareModal textures={compareList} onClose={() => setShowCompare(false)} />
      )}

      <SwatchZoomModal color={zoomColor} open={!!zoomColor} onOpenChange={(o) => !o && setZoomColor(null)} />

      {/* Favorites Drawer */}
      <AnimatePresence>
        {showFavs && (
          <FavoritesDrawer
            favColors={favColors}
            favIds={favIds}
            loading={loadingFavs}
            onClose={() => setShowFavs(false)}
            onRemove={removeFav}
            onShare={shareFavorites}
            onClearAll={clearFavs}
            onQuote={() => setQuoteOpen(true)}
            missing={favMissing}
          />
        )}
      </AnimatePresence>

      {/* Results */}
      {loading && !data ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>
      ) : !data?.collections?.length ? (
        <div className="text-center py-20 text-neutral-400">
          <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No fabrics found{search && ` matching "${search}"`}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.collections.map(coll => (
            <CollectionSection
              key={coll.collection_id}
              coll={coll}
              expanded={expandedColl[coll.collection_id]}
              onToggle={() => toggleColl(coll.collection_id)}
              expandedMat={expandedMat}
              onToggleMat={toggleMat}
              onCompare={toggleCompare}
              isInCompare={isInCompare}
              onToggleFav={toggleFav}
              isFav={isFav}
              onZoom={openZoom}
              showPrices={(data.price_brand_ids || []).includes(coll.brand_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Favorites Drawer ── */
const FavoritesDrawer = ({ favColors, favIds, loading, onClose, onRemove, onShare, onClearAll, onQuote, missing = [] }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
    onClick={onClose}
  >
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl overflow-y-auto"
      onClick={e => e.stopPropagation()}
      data-testid="favorites-drawer"
    >
      <div className="sticky top-0 bg-white border-b border-neutral-200 p-5 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          <Heart className="w-5 h-5 fill-rose-500 text-rose-500" />
          <h2 className="text-lg font-semibold text-neutral-900">My Favorites ({favIds.length})</h2>
        </div>
        <div className="flex items-center gap-2">
          {favIds.length > 0 && (
            <>
              <Button size="sm" variant="outline" onClick={onShare} className="gap-1.5 rounded-full text-xs" data-testid="share-favorites-btn">
                <Share2 className="w-3.5 h-3.5" />
                Share
              </Button>
              <Button size="sm" onClick={onQuote} disabled={!favColors.length} className="gap-1.5 rounded-full text-xs bg-neutral-900 hover:bg-neutral-800" data-testid="favorites-quote-btn">
                <FileText className="w-3.5 h-3.5" />
                Request a quote for these
              </Button>
              <Button size="sm" variant="ghost" onClick={onClearAll} className="gap-1.5 rounded-full text-xs text-rose-600 hover:bg-rose-50" data-testid="clear-favorites-btn">
                <Trash2 className="w-3.5 h-3.5" />
                Clear all
              </Button>
            </>
          )}
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>
        ) : favIds.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="w-12 h-12 mx-auto mb-3 text-neutral-200" />
            <p className="text-neutral-500">No favorites yet</p>
            <p className="text-sm text-neutral-400 mt-1">Click the heart icon on any fabric to save it here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {missing.map(code => (
              <div key={code} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-xl border border-dashed border-neutral-300" data-testid={`fav-missing-${code}`}>
                <div className="w-16 h-16 rounded-lg bg-neutral-200 flex items-center justify-center flex-shrink-0"><ShoppingBag className="w-5 h-5 text-neutral-400" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-neutral-700 font-mono truncate">{code}</p>
                  <p className="text-[11px] text-neutral-400 mt-0.5">This fabric is no longer in the catalog</p>
                </div>
                <button onClick={() => onRemove(code)} className="p-2 hover:bg-rose-50 rounded-lg transition-colors" data-testid={`remove-fav-${code}`} title="Remove">
                  <Trash2 className="w-4 h-4 text-rose-400" />
                </button>
              </div>
            ))}
            {favColors.map(color => {
              const imgSrc = resolveImageUrl(color.image_url, 512);
              return (
                <div key={color.source_color_id || color.id} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-xl border border-neutral-200" data-testid={`fav-item-${color.source_color_id}`}>
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-neutral-200 flex-shrink-0">
                    {imgSrc && <img src={imgSrc} alt={color.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-neutral-900 truncate">{color.name}</p>
                    <p className="text-[11px] text-neutral-400 font-mono">{color.code}</p>
                    {color.collection_name && (
                      <p className="text-[11px] text-neutral-500 mt-0.5">{color.collection_name}</p>
                    )}
                    <div className="flex gap-1 mt-1">
                      {color.composition && (
                        <span className="text-[9px] bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-full">{color.composition}</span>
                      )}
                      {color.width_cm && (
                        <span className="text-[9px] bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-full">{color.width_cm}cm</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => onRemove(color.source_color_id)}
                    className="p-2 hover:bg-rose-50 rounded-lg transition-colors"
                    data-testid={`remove-fav-${color.source_color_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-rose-400" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  </motion.div>
);

/* ── Collection Section ── */
const CollectionSection = ({ coll, expanded, onToggle, expandedMat, onToggleMat, onCompare, isInCompare, onToggleFav, isFav, onZoom, showPrices }) => (
  <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden" data-testid={`collection-${coll.collection_id}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 px-5 py-4 hover:bg-neutral-50 transition-colors text-left"
    >
      {expanded ? <ChevronDown className="w-5 h-5 text-neutral-400" /> : <ChevronRight className="w-5 h-5 text-neutral-400" />}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-semibold text-neutral-900">{coll.collection_name.replace(/-/g, '\u2011')}</span>
          <span className="text-xs text-neutral-400 bg-neutral-100 px-2 py-0.5 rounded-full">{brandLabel(coll.brand_name)}</span>
        </div>
        <span className="text-xs text-neutral-500">{coll.materials.some(m => m.legacy) ? `${coll.color_count} colors` : `${coll.material_count} ${(coll.material_count === 1 ? tierLabels(coll.brand_name).tier3 : tierLabels(coll.brand_name).tier3Plural).toLowerCase()} · ${coll.color_count} ${coll.color_count === 1 ? 'color' : 'colors'}`}</span>
        <StockDot status={coll.stock_status} discontinued={coll.discontinued} testId={`collection-stock-${coll.collection_id}`} side="bottom" />
      </div>
      <div className="flex gap-1 shrink-0">
        {coll.materials.slice(0, 5).map((m, mi) => (
          <div key={m.id} className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg border border-neutral-200 overflow-hidden ${mi >= 3 ? 'hidden sm:block' : ''}`}>
            {m.image_url && <img src={resolveImageUrl(m.image_url, 128)} alt={m.name ? `${m.name} fabric` : ''} className="w-full h-full object-cover" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />}
          </div>
        ))}
        {coll.materials.length > 3 && <span className="text-xs text-neutral-400 self-center sm:hidden">+{coll.materials.length - 3}</span>}
        {coll.materials.length > 5 && <span className="text-xs text-neutral-400 self-center hidden sm:inline">+{coll.materials.length - 5}</span>}
      </div>
    </button>

    <AnimatePresence>
      {expanded && (
        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
          <div className="border-t border-neutral-100 px-4 py-3 space-y-2">
            {coll.materials.length === 1 && coll.materials[0].legacy ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 py-1" data-testid={`legacy-colors-${coll.collection_id}`}>
                {coll.materials[0].colors.map(color => (
                  <FabricColorCard
                    key={color.id}
                    color={color}
                    onCompare={onCompare}
                    isInCompare={isInCompare}
                    onToggleFav={onToggleFav}
                    isFav={isFav}
                    onZoom={() => onZoom({ ...color, brand_name: coll.brand_name, collection_name: coll.collection_name, material_name: '' })}
                    showPrices={showPrices}
                  />
                ))}
              </div>
            ) : coll.materials.map(mat => (
              <MaterialCard
                key={mat.id}
                mat={mat}
                expanded={expandedMat[mat.id]}
                onToggle={() => onToggleMat(mat.id)}
                onCompare={onCompare}
                isInCompare={isInCompare}
                onToggleFav={onToggleFav}
                isFav={isFav}
                onZoom={onZoom}
                showPrices={showPrices}
                collectionName={coll.collection_name}
                brandName={coll.brand_name}
              />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/* ── Material Card ── */
const MaterialCard = ({ mat, expanded, onToggle, onCompare, isInCompare, onToggleFav, isFav, onZoom, showPrices, collectionName, brandName }) => (
  <div className="border border-neutral-100 rounded-lg overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 transition-colors text-left"
    >
      {expanded ? <ChevronDown className="w-4 h-4 text-neutral-400" /> : <ChevronRight className="w-4 h-4 text-neutral-400" />}
      <div className="w-10 h-10 rounded-lg border border-neutral-200 overflow-hidden flex-shrink-0">
        {mat.image_url && <img src={resolveImageUrl(mat.image_url, 128)} alt={mat.name ? `${mat.name} fabric` : ''} className="w-full h-full object-cover" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />}
      </div>
      <div className="flex-1 min-w-0">
        <span className="font-medium text-neutral-800 flex items-center gap-2">{mat.name}<StockDot status={mat.stock_status} discontinued={mat.discontinued} testId={`material-stock-${mat.id}`} side="bottom" /></span>
        <span className="text-xs text-neutral-400 ml-2">({mat.color_count} colors)</span>
        {showPrices && mat.base_price && (
          <span className="text-xs text-emerald-600 font-medium ml-2" data-testid={`material-price-${mat.id}`}>
            ${mat.base_price.toFixed(2)}/sq ft
          </span>
        )}
      </div>
      <div className="flex gap-0.5">
        {mat.colors.slice(0, 8).map(c => (
          <div key={c.id} className="w-6 h-6 rounded border border-neutral-200 overflow-hidden">
            <img src={resolveImageUrl(c.image_url, 128)} alt={c.name ? `${c.name} fabric swatch` : ''} className="w-full h-full object-cover" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
          </div>
        ))}
      </div>
    </button>

    <AnimatePresence>
      {expanded && (
        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
          <div className="border-t border-neutral-50 px-4 py-4 bg-neutral-50/50">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {mat.colors.map(color => (
                <FabricColorCard
                  key={color.id}
                  color={color}
                  onCompare={onCompare}
                  isInCompare={isInCompare}
                  onToggleFav={onToggleFav}
                  isFav={isFav}
                  onZoom={() => onZoom({ ...color, brand_name: brandName, collection_name: collectionName, material_name: mat.legacy ? '' : mat.name })}
                  showPrices={showPrices}
                />
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/* ── Individual Fabric Color Card ── */
const FabricColorCard = ({ color, onCompare, isInCompare, onToggleFav, isFav, onZoom, showPrices }) => {
  const imgSrc = resolveImageUrl(color.image_url, 512);
  const key = color.code || color.id;
  const selected = isInCompare(key);
  const favorited = isFav(key);

  return (
    <div
      className={`group rounded-xl border overflow-hidden bg-white transition-all hover:shadow-lg cursor-pointer ${
        selected ? 'border-amber-400 ring-2 ring-amber-200' : 'border-neutral-200'
      }`}
      onClick={() => onCompare(color)}
      data-testid={`fabric-color-${key}`}
    >
      <div className="aspect-square bg-neutral-100 overflow-hidden relative">
        {imgSrc
          ? <img src={imgSrc} alt={color.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
          : <div className="w-full h-full" style={{ backgroundColor: color.hex_color || '#e5e5e5' }} />}
        <StockDot status={color.stock_status} discontinued={color.discontinued} className="absolute bottom-2 left-2" testId={`color-stock-${color.code || color.id}`} />
        <button
          onClick={(e) => { e.stopPropagation(); onZoom(); }}
          className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-white/90 text-neutral-700 flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 hover:bg-amber-500 hover:text-white transition-all"
          aria-label="Enlarge swatch"
          title="Enlarge"
          data-testid={`zoom-${color.code || color.id}`}
        >
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
        {selected && (
          <div className="absolute top-2 right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow">
            <ArrowRightLeft className="w-3.5 h-3.5" />
          </div>
        )}
        {/* Favorite heart button */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleFav(key); }}
          className={`absolute top-2 left-2 w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-all ${
            favorited
              ? 'bg-rose-500 text-white scale-110'
              : 'bg-white/80 text-neutral-400 opacity-0 group-hover:opacity-100 hover:bg-rose-50 hover:text-rose-500'
          }`}
          data-testid={`fav-toggle-${key}`}
        >
          <Heart className={`w-3.5 h-3.5 ${favorited ? 'fill-current' : ''}`} />
        </button>
      </div>
      <div className="p-2.5">
        <p className="font-medium text-sm text-neutral-900 truncate">{color.name}</p>
        <p className="text-[11px] text-neutral-400 font-mono truncate">{color.code}</p>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {color.composition && (
            <span className="inline-flex items-center gap-0.5 text-[9px] bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-full">
              <FlaskConical className="w-2.5 h-2.5" />{color.composition.replace('100% ', '')}
            </span>
          )}
          {color.width_cm && (
            <span className="inline-flex items-center gap-0.5 text-[9px] bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-full">
              <Ruler className="w-2.5 h-2.5" />{color.width_cm}cm
            </span>
          )}
          {showPrices && color.price_per_sqft && (
            <span className="inline-flex items-center gap-0.5 text-[9px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">
              <Scale className="w-2.5 h-2.5" />${color.price_per_sqft}/sqft
            </span>
          )}
        </div>
        {color.webshop_url && (
          <a href={color.webshop_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] text-amber-600 hover:text-amber-800 mt-1.5"
            onClick={e => e.stopPropagation()}>
            <ExternalLink className="w-2.5 h-2.5" />Product details
          </a>
        )}
      </div>
    </div>
  );
};

/* ── Texture Compare Modal ── */
const TextureCompareModal = ({ textures, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-white rounded-2xl p-6 max-w-5xl w-full mx-4 max-h-[85vh] overflow-y-auto shadow-2xl"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-neutral-900 flex items-center gap-2">
          <ArrowRightLeft className="w-6 h-6 text-amber-600" />
          Fabric Comparison
        </h2>
        <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full"><X className="w-5 h-5" /></button>
      </div>

      <div className={`grid gap-6 ${textures.length === 2 ? 'grid-cols-2' : textures.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
        {textures.map(tex => {
          const imgSrc = resolveImageUrl(tex.image_url, 512);
          return (
            <div key={tex.code || tex.id} className="text-center">
              <div className="aspect-square rounded-xl overflow-hidden border border-neutral-200 mb-3 bg-neutral-100">
                <img src={imgSrc} alt={tex.name} className="w-full h-full object-cover" />
              </div>
              <h3 className="font-semibold text-neutral-900">{tex.name}</h3>
              <p className="text-xs text-neutral-500 font-mono">{tex.code}</p>
              <div className="mt-2 space-y-1 text-xs text-neutral-600">
                {tex.composition && <div className="flex items-center justify-center gap-1"><FlaskConical className="w-3 h-3 text-neutral-400" />{tex.composition}</div>}
                {tex.width_cm && <div className="flex items-center justify-center gap-1"><Ruler className="w-3 h-3 text-neutral-400" />{tex.width_cm}cm width</div>}
                {tex.price_per_sqft && <div className="flex items-center justify-center gap-1"><Scale className="w-3 h-3 text-emerald-500" />${tex.price_per_sqft}/sq ft</div>}
              </div>
              {tex.webshop_url && (
                <a href={tex.webshop_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 mt-2">
                  <ExternalLink className="w-3 h-3" />View details
                </a>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  </div>
);

export default PublicFabricBrowser;
